/**
 * Account settings — left nav, visibility, sub-route shell mount.
 */

import { escapeHtml } from "../../dom.js";
import { ROLE_WEIGHT } from "../../constants.js";
import { catalogEnabled, catalogAnyEnabled } from "../../capability-gate.js";
import {
  buildAccountSettingsNavHtml,
  buildAccountSettingsSubrouteShellHtml,
} from "./AccountSettingsShell.template.js";
import { ensureAccountSettingsStyles } from "./account-settings-styles.js";

/** @param {object} spec @param {object} me @param {(min: string) => boolean} hasRoleFn @param {(cap: string) => boolean} canFn */
export function settingsNavItemVisible(spec, me, hasRoleFn, canFn) {
  if (!me) return false;
  if (Array.isArray(spec.navCatalogAny) && spec.navCatalogAny.length) {
    if (!catalogAnyEnabled(me, spec.navCatalogAny)) return false;
  } else if (spec.navCatalogId && !catalogEnabled(me, spec.navCatalogId)) {
    return false;
  }
  if (spec.navGate && !me[spec.navGate]) return false;
  if (spec.navRequiresCap && typeof canFn === "function" && !canFn(spec.navRequiresCap)) return false;
  if (spec.hideChildAdmin && String(me.manager_admin || "").trim()) return false;
  const r = String(me.role || "").trim();
  if (r === "sa_staff") {
    if (spec.hideSaStaff) return false;
    const caps = new Set((me.sa_capabilities || []).map(String));
    const capOk = (need) => caps.has("sa_full_access") || caps.has(need);
    if (spec.navCap) return capOk(spec.navCap);
    if (String(spec.min || "") === "superadmin" && spec.saStaffNav) {
      return Boolean(me.ops_dashboard_eligible);
    }
    return ROLE_WEIGHT[r] >= ROLE_WEIGHT[spec.min || "user"];
  }
  return hasRoleFn(spec.min || "user");
}

/** Route ids that belong under the Settings hub (not Account). */
export const SETTINGS_HUB_ROUTE_IDS = Object.freeze(["settings", "admin", "billing", "telegram", "voice", "response-plans"]);

/**
 * Account page left nav — profile & personal preferences only.
 * @param {{ activeNavId?: string }} [ctx]
 */
export function buildAccountNavItems(_ctx = {}) {
  return [
    { id: "profile", label: "Profile", icon: "◉", section: "profile", href: "#/account" },
    { id: "notifications", label: "Alarm emails", icon: "✉", section: "notifications", href: "#/account" },
    { id: "security", label: "Security", icon: "◈", section: "security", href: "#/account" },
    { id: "side", label: "Account actions", icon: "⚙", section: "side", href: "#/account" },
  ];
}

/**
 * Settings hub left nav — admin, billing, telegram.
 * @param {{ me?: object, hasRole?: (min: string) => boolean, can?: (cap: string) => boolean }} ctx
 */
export function buildSettingsHubNavItems(ctx = {}) {
  const me = ctx.me || {};
  const hasRoleFn = ctx.hasRole || (() => false);
  const canFn = ctx.can || (() => false);
  /** @type {Array<{ id: string, label: string, icon?: string, href?: string, section?: string }>} */
  const items = [
    { id: "hub", label: "Overview", icon: "◎", section: "hub", href: "#/settings" },
  ];
  if (settingsNavItemVisible({ min: "admin", navCap: "sa_user_admin" }, me, hasRoleFn, canFn)) {
    items.push({ id: "admin", label: "Admin & users", icon: "☼", section: "admin", href: "#/settings" });
  }
  if (settingsNavItemVisible({ min: "admin", navCap: "sa_billing_admin", navGate: "billing_enabled" }, me, hasRoleFn, canFn)) {
    items.push({ id: "billing", label: "Billing", icon: "$", section: "billing", href: "#/settings" });
  }
  if (settingsNavItemVisible({ min: "admin", navGate: "voice_enabled" }, me, hasRoleFn, canFn)) {
    items.push({ id: "voice", label: "Voice", icon: "☎", section: "voice", href: "#/settings" });
  }
  if (settingsNavItemVisible({ min: "admin" }, me, hasRoleFn, canFn)) {
    items.push({ id: "response-plans", label: "Response plans", icon: "⎙", href: "#/response-plans" });
  }
  if (hasRoleFn("user")) {
    items.push({ id: "telegram", label: "Telegram", icon: "✆", section: "telegram", href: "#/settings" });
  }
  return items;
}

/**
 * @param {ParentNode} root
 * @param {string} [activeNavId]
 * @returns {() => void}
 */
export function wireSettingsHubNav(root, activeNavId = "hub") {
  if (!root) return () => {};
  const setActive = (id) => {
    root.querySelectorAll(".acc-settings-nav__link").forEach((el) => {
      const navId = el.getAttribute("data-acc-nav") || el.getAttribute("data-acc-nav-id");
      el.classList.toggle("acc-settings-nav__link--active", navId === id);
    });
  };

  const onNavClick = (ev) => {
    const btn = ev.target.closest("[data-acc-nav]");
    if (!btn || !root.contains(btn)) return;
    ev.preventDefault();
    const id = btn.getAttribute("data-acc-nav");
    if (id === "hub") {
      const top = root.querySelector("#settings-sec-hub") || root.querySelector(".settings-overview-stack");
      top?.scrollIntoView({ behavior: "smooth", block: "start" });
      setActive("hub");
      return;
    }
    const target = root.querySelector(`#settings-sec-${id}`) || root.querySelector(`[data-settings-section="${id}"]`);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      setActive(id);
    }
  };

  root.addEventListener("click", onNavClick);
  setActive(activeNavId);

  return () => {
    root.removeEventListener("click", onNavClick);
  };
}

/** @deprecated use buildAccountNavItems or buildSettingsHubNavItems */
export function buildAccountSettingsNavItems(ctx = {}) {
  return buildAccountNavItems(ctx).concat(
    buildSettingsHubNavItems(ctx).filter((it) => it.id !== "hub"),
  );
}

/**
 * @param {ParentNode} root
 * @param {string} [activeNavId]
 * @returns {() => void}
 */
export function wireAccountSettingsNav(root, activeNavId = "profile") {
  if (!root) return () => {};
  const setActive = (id) => {
    root.querySelectorAll(".acc-settings-nav__link").forEach((el) => {
      const navId = el.getAttribute("data-acc-nav") || el.getAttribute("data-acc-nav-id");
      el.classList.toggle("acc-settings-nav__link--active", navId === id);
    });
  };

  const onNavClick = (ev) => {
    const btn = ev.target.closest("[data-acc-nav]");
    if (!btn || !root.contains(btn)) return;
    ev.preventDefault();
    const id = btn.getAttribute("data-acc-nav");
    const target = root.querySelector(`#acc-sec-${id}`) || root.querySelector(`[data-acc-section="${id}"]`);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      setActive(id);
    }
  };

  root.addEventListener("click", onNavClick);
  setActive(activeNavId);

  return () => {
    root.removeEventListener("click", onNavClick);
  };
}

/**
 * Build Settings sub-route HTML (settings hub / admin / billing / telegram).
 * @param {{ activeNavId: string, contentHtml: string, me?: object, roleLabel?: string, username?: string, hasRole?: (min: string) => boolean, can?: (cap: string) => boolean, navTitle?: string }} opts
 * @returns {string}
 */
export function buildAccountSettingsSubrouteHtml(opts) {
  ensureAccountSettingsStyles();
  const me = opts.me || {};
  const navItems = buildSettingsHubNavItems({
    me,
    hasRole: opts.hasRole,
    can: opts.can,
  });
  const navHtml = opts.navHtml || buildAccountSettingsNavHtml(navItems, opts.activeNavId, false);
  return buildAccountSettingsSubrouteShellHtml({
    navTitle: opts.navTitle || "Settings",
    roleLabel: opts.roleLabel || "—",
    username: opts.username || escapeHtml(String(me.username || "—")),
    navHtml,
    contentHtml: opts.contentHtml || "",
  });
}

/**
 * @param {{ me?: object, hasRole?: (min: string) => boolean, can?: (cap: string) => boolean }} ctx
 * @returns {string}
 */
export function buildSettingsHubCardsHtml(ctx = {}) {
  const me = ctx.me || {};
  const hasRoleFn = ctx.hasRole || (() => false);
  const canFn = ctx.can || (() => false);
  const cards = [];
  if (settingsNavItemVisible({ min: "admin", navCap: "sa_user_admin" }, me, hasRoleFn, canFn)) {
    cards.push(`<button type="button" data-cc-go="admin" class="glass-panel glass-panel--panel acc-settings-hub-card">
      <span class="acc-settings-hub-card__ico" aria-hidden="true">☼</span>
      <span class="acc-settings-hub-card__body">
        <strong>Admin &amp; users</strong>
        <span class="muted">Manage users, roles, and account access</span>
      </span>
      <span aria-hidden="true">›</span>
    </button>`);
  }
  if (settingsNavItemVisible({ min: "admin", navCap: "sa_billing_admin", navGate: "billing_enabled" }, me, hasRoleFn, canFn)) {
    cards.push(`<button type="button" data-cc-go="billing" class="glass-panel glass-panel--panel acc-settings-hub-card">
      <span class="acc-settings-hub-card__ico" aria-hidden="true">$</span>
      <span class="acc-settings-hub-card__body">
        <strong>Billing</strong>
        <span class="muted">Invoices and payment history</span>
      </span>
      <span aria-hidden="true">›</span>
    </button>`);
  }
  if (settingsNavItemVisible({ min: "admin", navGate: "voice_enabled" }, me, hasRoleFn, canFn)) {
    cards.push(`<button type="button" data-cc-go="voice" class="glass-panel glass-panel--panel acc-settings-hub-card">
      <span class="acc-settings-hub-card__ico" aria-hidden="true">☎</span>
      <span class="acc-settings-hub-card__body">
        <strong>Voice</strong>
        <span class="muted">Emergency phone alerts</span>
      </span>
      <span aria-hidden="true">›</span>
    </button>`);
  }
  if (hasRoleFn("user")) {
    cards.push(`<button type="button" data-cc-go="telegram" class="glass-panel glass-panel--panel acc-settings-hub-card">
      <span class="acc-settings-hub-card__ico" aria-hidden="true">✆</span>
      <span class="acc-settings-hub-card__body">
        <strong>Telegram</strong>
        <span class="muted">Connect bot alerts to your chats</span>
      </span>
      <span aria-hidden="true">›</span>
    </button>`);
  }
  if (!cards.length) {
    return `<div class="glass-panel glass-panel--panel acc-settings-panel"><p class="muted" style="margin:0">No settings modules available for your role.</p></div>`;
  }
  return `<div class="acc-settings-hub-cards">${cards.join("")}</div>`;
}
